/**
 * Dashboard Command Store
 * State management for the ultimate mission control dashboard
 */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

interface FocusPriority {
  id: string
  title: string
  reason: string
  estimatedMinutes: number
}

interface Task {
  id: string
  title: string
  status: string
  priority: string
  dueDate: string | null
  estimatedMinutes: number
  project?: string
  column?: string
  isBlocked: boolean
  blockedReason?: string
  riskScore: number
}

interface CommandMatrix {
  now: Task[]
  today: Task[]
  tomorrow: Task[]
}

interface Insight {
  id: string
  type: string
  priority: string
  title: string
  content: string
  actionUrl?: string
  actionLabel?: string
  metadata: any
}

interface VelocityMetrics {
  velocityScore: number
  tasksCompleted: number
  focusTimeMinutes: number
  interruptionCount: number
  taskCompletionRate: number
  peakHours: number[]
}

interface TeamMember {
  id: string
  name: string
  status: string
  statusText: string
  currentFocus?: string
  needsHelp: boolean
  workload: number
  availableToHelp: boolean
}

interface DashboardCommandState {
  // View state
  currentView: 'command' | 'classic' | 'minimal'
  timeRange: 'day' | 'week' | 'sprint'
  focusMode: boolean
  
  // Data
  focusPriority: FocusPriority | null
  commandMatrix: CommandMatrix
  insights: Insight[]
  velocityMetrics: VelocityMetrics | null
  teamRadar: TeamMember[]
  
  // Statistics
  statistics: {
    critical: number
    urgent: number
    totalToday: number
    streak: number
  }
  
  // User status
  userStatus: {
    status: string
    currentFocus?: string
    workload: number
  } | null
  
  // UI State
  commandBarOpen: boolean
  commandHistory: string[]
  draggedTaskId: string | null
  selectedInsightId: string | null
  
  // Actions
  setView: (view: 'command' | 'classic' | 'minimal') => void
  setTimeRange: (range: 'day' | 'week' | 'sprint') => void
  toggleFocusMode: () => void
  setFocusPriority: (priority: FocusPriority | null) => void
  updateCommandMatrix: (matrix: CommandMatrix) => void
  addInsight: (insight: Insight) => void
  dismissInsight: (id: string) => void
  updateVelocityMetrics: (metrics: VelocityMetrics) => void
  updateTeamRadar: (members: TeamMember[]) => void
  updateStatistics: (stats: any) => void
  updateUserStatus: (status: any) => void
  
  // Command bar
  toggleCommandBar: () => void
  addToCommandHistory: (command: string) => void
  
  // Drag and drop
  setDraggedTask: (taskId: string | null) => void
  moveTask: (taskId: string, from: 'now' | 'today' | 'tomorrow', to: 'now' | 'today' | 'tomorrow') => void
  
  // Real-time updates
  handleRealtimeUpdate: (update: any) => void
  
  // Reset
  reset: () => void
}

const initialState = {
  currentView: 'command' as const,
  timeRange: 'day' as const,
  focusMode: false,
  focusPriority: null,
  commandMatrix: {
    now: [],
    today: [],
    tomorrow: []
  },
  insights: [],
  velocityMetrics: null,
  teamRadar: [],
  statistics: {
    critical: 0,
    urgent: 0,
    totalToday: 0,
    streak: 0
  },
  userStatus: null,
  commandBarOpen: false,
  commandHistory: [],
  draggedTaskId: null,
  selectedInsightId: null
}

export const useDashboardCommandStore = create<DashboardCommandState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        
        // View actions
        setView: (view) => set({ currentView: view }),
        setTimeRange: (range) => set({ timeRange: range }),
        toggleFocusMode: () => set((state) => ({ focusMode: !state.focusMode })),
        
        // Data actions
        setFocusPriority: (priority) => set({ focusPriority: priority }),
        updateCommandMatrix: (matrix) => set({ commandMatrix: matrix }),
        
        addInsight: (insight) => set((state) => ({
          insights: [insight, ...state.insights].slice(0, 10) // Keep max 10 insights
        })),
        
        dismissInsight: (id) => set((state) => ({
          insights: state.insights.filter(i => i.id !== id)
        })),
        
        updateVelocityMetrics: (metrics) => set({ velocityMetrics: metrics }),
        updateTeamRadar: (members) => set({ teamRadar: members }),
        updateStatistics: (stats) => set({ statistics: stats }),
        updateUserStatus: (status) => set({ userStatus: status }),
        
        // Command bar
        toggleCommandBar: () => set((state) => ({ 
          commandBarOpen: !state.commandBarOpen 
        })),
        
        addToCommandHistory: (command) => set((state) => ({
          commandHistory: [command, ...state.commandHistory].slice(0, 20) // Keep last 20 commands
        })),
        
        // Drag and drop
        setDraggedTask: (taskId) => set({ draggedTaskId: taskId }),
        
        moveTask: (taskId, from, to) => set((state) => {
          const matrix = { ...state.commandMatrix }
          const task = matrix[from].find(t => t.id === taskId)
          
          if (!task) return state
          
          // Remove from source
          matrix[from] = matrix[from].filter(t => t.id !== taskId)
          
          // Add to destination
          matrix[to] = [...matrix[to], task]
          
          return { commandMatrix: matrix }
        }),
        
        // Real-time updates
        handleRealtimeUpdate: (update) => {
          const state = get()
          
          switch (update.type) {
            case 'task_updated':
              // Update task in command matrix
              const matrix = { ...state.commandMatrix }
              for (const key of ['now', 'today', 'tomorrow'] as const) {
                const index = matrix[key].findIndex(t => t.id === update.taskId)
                if (index !== -1) {
                  matrix[key][index] = { ...matrix[key][index], ...update.data }
                  set({ commandMatrix: matrix })
                  break
                }
              }
              break
              
            case 'team_pulse_updated':
              // Update team member status
              const members = state.teamRadar.map(m => 
                m.id === update.userId 
                  ? { ...m, ...update.data }
                  : m
              )
              set({ teamRadar: members })
              break
              
            case 'new_insight':
              // Add new insight
              get().addInsight(update.insight)
              break
              
            case 'metrics_updated':
              // Update velocity metrics
              if (update.metrics) {
                set({ velocityMetrics: update.metrics })
              }
              break
          }
        },
        
        // Reset
        reset: () => set(initialState)
      }),
      {
        name: 'dashboard-command-storage',
        partialize: (state) => ({
          currentView: state.currentView,
          timeRange: state.timeRange,
          focusMode: state.focusMode,
          commandHistory: state.commandHistory
        })
      }
    ),
    {
      name: 'DashboardCommandStore'
    }
  )
)